import thumbDrafOrder from '@assets/images/thumb_draft_order.png'
import thumbInvoice from '@assets/images/thumb_invoice.png'
import thumbOrder from '@assets/images/thumb_order.png'
import thumbPackingSlip from '@assets/images/thumb_packing_slip.png'
import thumbRefund from '@assets/images/thumb_refund.png'

import { SettingComponentMap } from '@bundles/constant'
import {
    hideToast as hideAppBridgeToast,
    showToast as showAppBridgeToast,
} from '@bundles/libs/shopify/appBridge'
import { RequestFeatureStatus } from '@bundles/sharedTypes/modelRequestFeature'
import type { Tone } from '@shopify/polaris/build/ts/src/components/Badge'

export const handleConvertToQueryParameter = ( params?: Record<string, unknown> | null ) => {
  if ( !params || typeof params !== 'object' || Object.keys( params ).length === 0 ) {
    return '';
  }
  const objectList = Object.entries( params );
  const arrayParams = objectList.filter( ( el ) => {
    if ( Array.isArray( el[1] ) ) {
      return el[1].length > 0;
    } else {
      return el[1];
    }
  } );

  const result = arrayParams.reduce( ( result, current, index ) => {
    if ( Array.isArray( current[1] ) ) {
      const query = current[1].map( ( el ) => {
        return `${current[0]}[]=${el}`;
      } );
      return result + `${index === 0 ? "" : "&"}` + query.join( "&" );
    } else {
      const query = `${current[0]}=${current[1]}`;
      return result + `${index === 0 ? '' : '&'}` + query;
    }
  }, `` );

  return result;
};

export const resolveConfig = ( comp: string ) => {
  type SettingComponentConfig = ( typeof SettingComponentMap )[keyof typeof SettingComponentMap];
  const mapByStringKey = SettingComponentMap as Record<string, SettingComponentConfig>;
  const directConfig = mapByStringKey[comp];
  if ( directConfig ) return directConfig;
  const entries = Object.entries( SettingComponentMap ) as [string, SettingComponentConfig][];
  for ( const [key, config] of entries ) {
    if ( key.includes( "*" ) ) {
      const regex = new RegExp(
        "^" + key.replace( /\./g, "\\." ).replace( /\*/g, ".*" ) + "$"
      );
      if ( regex.test( comp ) ) {
        return config;
      }
    }
  }

  return null;
}

export const resolveDataComp = ( field: string ) => {
  const dataComp = Object.entries( SettingComponentMap ).find( ( [_key, { scrollTo }] ) => scrollTo === field );
  return dataComp ? dataComp[0] : ''
}

/**
 * Invoice LineItem flat (product-title, item-image, …) vs form cũ gom trong `item-column`
 * (draft_order / packing_slip / refund / Swanepoel).
 */
export const THEME_SETTING_ACTION_FALLBACKS: Record<string, string[]> = {
  "product-title": ["item-column"],
  "item-image": ["item-column"],
  "item-sku": ["item-column"],
  "item-barcode-value": ["item-column"],
  "item-vendor": ["item-column"],
  "item-qty": ["item-column"],
  "item-hs-code": ["item-column"],
  "item-checkbox": ["item-column"],
  "item-metafields": ["item-column"],
  "item-discount": ["price", "item-column"],
  "table-note": ["note"],
};

/**
 * Chọn CollapsibleList.Item `id` để mở: ưu tiên action map, fallback section cũ nếu
 * L2 menu chưa có `data-fd-section-id` của preferred (form legacy gom item-column).
 *
 * Không dùng `document.getElementById(id)` — L3 `detailBody` gắn `id={activeId}` và
 * Polaris `Collapsible id={id}` làm false-positive → deep-link đứng im ở L3 hiện tại.
 */
export function resolveThemeSettingOpenAction( preferredAction: string ): string {
  if ( typeof document === "undefined" || !preferredAction ) {
    return preferredAction;
  }
  const sectionExists = ( id: string ) =>
    Boolean(
      document.querySelector( `[data-fd-section-id="${CSS.escape( id )}"]` )
    );

  if ( sectionExists( preferredAction ) ) return preferredAction;

  for ( const id of THEME_SETTING_ACTION_FALLBACKS[preferredAction] ?? [] ) {
    if ( sectionExists( id ) ) return id;
  }
  // Preferred có thể mount sau khi mở — giữ action map, không fallback theo DOM L3.
  return preferredAction;
}

const TOP_LEVEL_FORM_ELEMENTS = new Set( [
  "BrandStyle",
  "Overview",
  "Supplier",
  "Client",
  "Billing",
  "LineItem",
  "LineItemNotes",
  "LineItemPrices",
  "LineItemTotals",
  "Footer",
  "Location",
  "TrackingInfo",
  "TimelineInfo",
  "DocumentSettings",
  /** Theme-specific L1 (e.g. Swanepoel Theme Personalize). */
  "ThemePersonalize",
] );

/** `LineItem.price.*` → L1 Prices; `LineItem.total.*` → L1 Totals; `LineItem.note.*` → L1 Notes; `LineItem.template-currency.*` → General settings. */
const LINE_ITEM_PRICES_SECTIONS = new Set( [ "price" ] );
const LINE_ITEM_TOTALS_SECTIONS = new Set( [ "total" ] );
const LINE_ITEM_NOTES_SECTIONS = new Set( [ "note" ] );

/**
 * Từ `data-comp` trên preview (vd. `LineItem.column.image`) → form cần mở trong theme builder (vd. `LineItem`).
 * Segment đầu của `data-comp` trùng tên Form (`TOP_LEVEL_FORM_ELEMENTS`), không dùng alias prefix.
 * Ngoại lệ: `LineItem.price.*` → `LineItemPrices`; `LineItem.total.*` → `LineItemTotals`;
 * `LineItem.note.*` → `LineItemNotes`; `LineItem.template-currency.*` → `DocumentSettings`.
 */
export function getFormElementFromDataComp( dataComp: string | undefined ): string | null {
  if ( !dataComp || typeof dataComp !== "string" ) return null;
  const trimmed = dataComp.trim();
  if ( !trimmed.includes( "." ) ) {
    return TOP_LEVEL_FORM_ELEMENTS.has( trimmed ) ? trimmed : null;
  }
  const parts = trimmed.split( "." );
  const first = parts[0];
  if ( first === "LineItem" && parts.length >= 2 ) {
    if ( parts[1] === "template-currency" ) {
      return "DocumentSettings";
    }
    if ( LINE_ITEM_PRICES_SECTIONS.has( parts[1] ) ) {
      return "LineItemPrices";
    }
    if ( LINE_ITEM_TOTALS_SECTIONS.has( parts[1] ) ) {
      return "LineItemTotals";
    }
    if ( LINE_ITEM_NOTES_SECTIONS.has( parts[1] ) ) {
      return "LineItemNotes";
    }
  }
  return TOP_LEVEL_FORM_ELEMENTS.has( first ) ? first : null;
}

export const timeAgo = ( isoDate: string ): string => {
  const now = new Date();
  const past = new Date( isoDate );
  const seconds = Math.floor( ( now.getTime() - past.getTime() ) / 1000 );

  const intervals = [
    { label: "month", seconds: 2592000 },
    { label: "week", seconds: 604800 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 },
  ];

  for ( const interval of intervals ) {
    const count = Math.floor( seconds / interval.seconds );
    if ( count >= 1 ) {
      return `${count} ${interval.label}${count > 1 ? "s" : ""} ago`;
    }
  }
  return "just now";
};
export const formatDate = ( isoDate: string ): string => {
  const date = new Date( isoDate );

  const formatted = new Intl.DateTimeFormat( "en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  } ).format( date );
  return formatted;
};
export const customParamsSerializer = ( params: Record<string, A> ): string => {
  const queryString = [];

  for ( const key in params ) {
    const value = params[key];

    if ( value == null || value === "" ) continue; // Bỏ qua param rỗng

    if ( typeof value === "object" && !Array.isArray( value ) ) {
      for ( const subKey in value ) {
        const subValue = value[subKey];
        if ( subValue != null && subValue !== "" ) {
          queryString.push( `${key}[${subKey}]=${encodeURIComponent( subValue )}` );
        }
      }
    } else {
      const encodedValue =
        key === "s"
          ? encodeURIComponent( value ).replace( /%20/g, " " )
          : encodeURIComponent( value );

      queryString.push( `${key}=${encodedValue}` );
    }
  }

  return queryString.join( "&" );
};
export const createArrayByCount = ( countArray: number ): number[] => {
  if ( typeof countArray !== "number" || countArray < 1 ) {
    return [];
  }

  return Array.from( { length: countArray }, ( _, i ) => i + 1 );
};

type ToastParam = {
  content: string;
  isError?: boolean;
  duration?: number;
  action?: string;
};
const toastIds = new Map<string, string>();
export const showToast = ( params: ToastParam ) => {
  if ( params.action === 'CLEAR' ) {
    hideAppBridgeToast( toastIds.get( params.content ) );
    toastIds.delete( params.content );
    return;
  }

  const toastId = showAppBridgeToast( {
    message: params.content,
    duration: params?.duration || 2000,
    isError: params?.isError,
  } );

  if ( toastId ) {
    toastIds.set( params.content, toastId );
  }
};
export const statusBadge = ( status: RequestFeatureStatus ): Tone | undefined => {

  switch ( status ) {
    case RequestFeatureStatus.Rejected:
      return 'critical';
    case RequestFeatureStatus.Approved:
    case RequestFeatureStatus.Planned:
      return undefined;
    case RequestFeatureStatus.Pending:
      return 'attention';
    case RequestFeatureStatus.Developing:
      return 'info';
    case RequestFeatureStatus.Completed:
      return 'success';
    default:
      return 'read-only';
  }
};

export const validateEmail = (emailStr: string) => {
  const validRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  const emails = emailStr.split(",");
  const testEmail = emails.filter((email) => !validRegex.test(email));
  return (emails.length > 10 || testEmail.length) && emailStr !== "";
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const getThemeThumbURl = (type: string) => {
  switch (type) {
    case 'invoice':
      return thumbInvoice
    case 'refund':
      return thumbRefund
    case 'draft_order':
      return thumbDrafOrder
    case 'order':
      return thumbOrder
    case 'packing_slip':
      return thumbPackingSlip
    default:
      break
  }
}
